import React, { useCallback, useEffect, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import useAlert from '../../Alerts/useAlert';
import DataTable from '../../DataTable/datatable.component';
import DateColumn from '../../DataTable/datatable.datecolumn';
import SelectFilter from '../../DataTable/datatable.selectfilter';
import { getSortOrder, getTableFilters } from '../../Utilities/SessionStorage';
import { correlationId, useHttpUtils } from '../../Utilities/useHttpUtils';
import { useNumberUtils } from '../../Utilities/useNumberUtils';

function ReportingLotListTable({ tableFilters, loadingFilters, setFilters }) {

    const { t } = useTranslation();
    const { setAlert } = useAlert();
    const { fetchDataAuthenticated } = useHttpUtils();

    const [loading, setLoading] = useState();
    const [data, setData] = useState([]);

    const { formatLongNumber } = useNumberUtils();

    const getLots = useCallback(async (dateFrom, dateTo) => {

        let params = new URLSearchParams();

        if (dateFrom && dateTo) {
            setLoading(true);
            params.append("dateFrom", dateFrom);
            params.append("dateTo", dateTo);

            if (tableFilters.selectedBusinessUnits.length > 0) {
                params.append("businessUnits", tableFilters.selectedBusinessUnits.map(bu => bu.code).join(","));
            }

            if (tableFilters.selectedLines.length > 0) {
                params.append("lines", tableFilters.selectedLines.map(l => l.productionLine).join(","));
            }

            await fetchDataAuthenticated("api/v1/lots/report?" + params.toString(), "GET")
                .then(async response => {
                    if (response.ok) {
                        return response.json();
                    }

                    throw new Error(`Failed to get lots [${correlationId}]`);
                })
                .then(data => {
                    setData(data);
                })
                .catch(error => {
                    setAlert("danger", "Error", error.message);
                })
                .finally(() => {
                    setLoading(false);
                });
        } else {
            // Do nothing if we have not filtered by date as this would return all data
        }
    }, [fetchDataAuthenticated, setAlert,  tableFilters.selectedBusinessUnits, tableFilters.selectedLines]);

    useEffect(() => {
        if (loadingFilters) {
            return;
        }
        getLots(tableFilters.dateFilter.from, tableFilters.dateFilter.to,);
    }, [getLots, loadingFilters, tableFilters.dateFilter.from, tableFilters.dateFilter.to]);

    const columns = [
        {
            Header: t('LotList.ColHead.BusinessUnit'),
            accessor: 'businessUnit',
            Filter: SelectFilter,
            filter: 'equals'
        },
        {
            Header: t('LotList.ColHead.LotNumber'),
            accessor: 'lotNumber',
            Cell: (props) => {
                return <Link to={{ pathname: `/lotdetails/${props.value}` }}>{props.value}</Link>;
            },
            filter: 'includesString'
        },
        {
            Header: t('LotList.ColHead.Grade'),
            accessor: 'grade',
            filter: 'includesString'
        },
        {
            Header: t('LotList.ColHead.Weight'),
            accessor: 'lotWeight',
            filter: 'NoFilter',
            Cell: ({ value }) => formatLongNumber(value)
        },
        {
            Header: t('LotList.ColHead.LoadoutWeight'),
            accessor: 'loadoutWeight',
            filter: 'NoFilter',
            Cell: ({ value, row }) => formatLongNumber(value) + (row.original.weightUnit == null ? '' : " " + row.original.weightUnit)
        },
        DateColumn({ header: t('LotList.ColHead.BuildStart'), accessor: 'buildStartUtc' }),
        DateColumn({ header: t('LotList.ColHead.BuildEnd'), accessor: 'buildEndUtc' }),
        {
            Header: t('LotList.ColHead.LastModifiedBy'),
            accessor: 'lastModifiedBy',
            filter: 'includesString'
        },
        {
            Header: t('LotList.ColHead.LastComment'),
            accessor: 'lastGradingComment',
            filter: 'includesString'
        },
        DateColumn({ header: t('LotList.ColHead.LastGradedUTC'), accessor: 'lastGradedUTC' }),
        {
            Header: t('LotList.ColHead.LastGradedReasonCode'),
            accessor: 'lastGradedReasonCode',
            filter: 'includesString'
        },
        ...data.some(l => l.data.rftCode)
            ? [
                {
                    Header: t('LotList.ColHead.RFTCode'),
                    accessor: 'data.rftCode',
                    filter: 'includesString'
                },
                {
                    Header: t('LotList.ColHead.LastRFTComment'),
                    accessor: 'lastRFTComment',
                    filter: 'includesString'
                }
            ]
            : [],
    ];

    const initialState = {
        pageSize: 20
    };

    const tableId = "reporting-lots-table";
    const sortBy = getSortOrder(tableId);
    const filters = getTableFilters(tableId);

    if (sortBy) {
        initialState.sortBy = sortBy;
    }

    if (filters) {
        initialState.filters = filters;
    }

    if (loading) {
        return (
            <div className="d-flex justify-content-center">
                <Spinner animation="border" />
            </div>
        );
    }

    if (!tableFilters.dateFilter?.from || !tableFilters.dateFilter?.to) {
        return (
            <h4 className="d-flex justify-content-center mt-5">{t("Reporting.LotList.SelectDateRange")}</h4>
        );
    }

    return data.length > 0
        ? <DataTable id={tableId} tableColumns={columns} tableData={data} initialState={initialState} setFilters={setFilters} />
        : <h4 className="d-flex justify-content-center mt-5">{t("LotList.NoRecords")}</h4>;
}

export default ReportingLotListTable;